// WHAT: Modal component for editing and creating tasks
// WHY:  Provides comprehensive form for task management with all task fields
// DOMAIN: Productivity → TimeTracQ
// DEPENDENCIES: Dialog from headlessui/react, Card, Button components
// RISKS:
//   - NOTE: Form validation may be incomplete - may allow invalid data
//   - NOTE: Delete handler is optional - may cause confusion
//   - NOTE: Form state management is complex - may have sync issues

import type { SupabaseTask } from "../../hooks/data/useSupabasetasks";

import { Dialog, DialogPanel, DialogTitle } from "@headlessui/react";
import { useState, useEffect } from "react";

import { Button } from "@/components/ui";

/**
 * Task form type (allows optional ID for new tasks)
 */
export type TaskForm = Omit<SupabaseTask, "id"> & { id?: string };

/**
 * Props for TaskEditModal component
 */
interface Properties {
  /** Close handler */
  onClose: () => void;
  /** Delete handler (optional) */
  onDelete?: (id: string) => Promise<void>;
  /** Save handler */
  onSave: (task: TaskForm) => Promise<void>;
  /** Whether modal is open */
  open: boolean;
  /** Preselected workspace (for new tasks) */
  preselectedWorkspace?: string;
  /** Existing task data (for edit mode) */
  task?: SupabaseTask;
}

/**
 * WHAT:
 *   Modal component that provides comprehensive form for editing and creating
 *   tasks with all task fields including workspace, aspect, status, priority,
 *   deadlines, and metadata.
 *
 * WHY:
 *   Enables full task management with all available fields in a modal interface.
 *
 * BEHAVIOR:
 *   - Supports both edit and create modes
 *   - Pre-fills form with task data in edit mode
 *   - Uses preselected workspace for new tasks
 *   - Provides save and delete actions
 *   - Manages form state locally
 *
 * RISKS:
 *   - NOTE: Form validation may be incomplete - may allow invalid data
 *   - NOTE: Delete handler is optional - may cause confusion
 *   - NOTE: Form state management is complex - may have sync issues
 *
 * @param root0
 *
 * @param root0.open
 *
 * @param root0.task
 *
 * @param root0.preselectedWorkspace
 *
 * @param root0.onSave
 *
 * @param root0.onDelete
 *
 * @param root0.onClose
 *
 * @returns {JSX.Element} Task edit modal component
 *
 */
export const TaskEditModal = ({
  onClose,
  onDelete,
  onSave,
  open,
  preselectedWorkspace,
  task,
}: Properties) => {
  const isEdit = !!task;
  const [form, setForm] = useState<TaskForm>(() => {
    const baseForm: Omit<TaskForm, "id"> = {
      aspect: task?.aspect ?? "",
      deadline: task?.deadline ?? "",
      effort: task?.effort ?? "",
      estimated_time: task?.estimated_time ?? null,
      impact: task?.impact ?? "",
      priority: task?.priority ?? "medium",
      progress: task?.progress ?? 0,
      score: task?.score ?? 5,
      status: task?.status ?? "Not Started",
      task: task?.task ?? "",
      workspace: task?.workspace ?? preselectedWorkspace ?? "",
    };
    if (task?.id) {
      return { ...baseForm, id: task.id };
    }
    return baseForm;
  });

  useEffect(() => {
    if (task) {
      setForm({
        aspect: task.aspect,
        deadline: task.deadline,
        effort: task.effort,
        estimated_time: task.estimated_time,
        id: task.id,
        impact: task.impact,
        priority: task.priority,
        progress: task.progress,
        score: task.score,
        status: task.status,
        task: task.task,
        workspace: task.workspace,
      });
    } else if (preselectedWorkspace) {
      // Update workspace when preselectedWorkspace changes for new tasks
      setForm((previous) => ({
        ...previous,
        workspace: preselectedWorkspace,
      }));
    }
  }, [task, preselectedWorkspace]);

  useEffect(() => {
    if (preselectedWorkspace && !task) {
      setForm((previous) => ({ ...previous, workspace: preselectedWorkspace }));
    }
  }, [preselectedWorkspace, task]);

  const handleChange = (key: keyof TaskForm, value: null | number | string) => {
    setForm((previous) => ({ ...previous, [key]: value }));
  };

  const handleSubmit = async () => {
    await onSave(form);
    onClose();
  };

  const handleDelete = async () => {
    if (!task || !onDelete) {
      return;
    }
    const confirmed = confirm("Delete this task?");
    if (!confirmed) {
      return;
    }
    await onDelete(task.id);
    onClose();
  };

  return (
    <Dialog className="relative z-50" onClose={onClose} open={open}>
      <div
        aria-hidden="true"
        className="fixed inset-0 bg-black/60 backdrop-blur-sm transition-opacity duration-300"
      />
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <DialogPanel
          className="bg-slate-800/90 border border-slate-600/50 text-white rounded-2xl p-8
                                 shadow-tracq-card w-full max-w-md
                                 transform transition-all duration-300 scale-100"
        >
          <DialogTitle className="text-2xl font-bold mb-8 text-center text-tracq-bright-blue">
            {isEdit ? "Edit Task" : "New Task"}
          </DialogTitle>

          <div className="space-y-6">
            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-300 block">Task</label>
              <input
                className="w-full px-4 py-3 bg-slate-700/50 border border-slate-500/50
                          rounded-xl text-white placeholder-slate-400
                          focus:border-tracq-bright-blue focus:ring-2 focus:ring-tracq-bright-blue/20
                          transition-all duration-200 outline-none"
                onChange={(e) => {
                  handleChange("task", e.target.value);
                }}
                placeholder="What needs to be done?"
                value={form.task}
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-300 block">Workspace</label>
              <input
                className="w-full px-4 py-3 bg-slate-700/50 border border-slate-500/50
                          rounded-xl text-white placeholder-slate-400
                          focus:border-tracq-bright-blue focus:ring-2 focus:ring-tracq-bright-blue/20
                          transition-all duration-200 outline-none"
                onChange={(e) => {
                  handleChange("workspace", e.target.value);
                }}
                placeholder="Project or workspace"
                value={form.workspace ?? ""}
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-300 block">Aspect</label>
              <input
                className="w-full px-4 py-3 bg-slate-700/50 border border-slate-500/50
                          rounded-xl text-white placeholder-slate-400
                          focus:border-tracq-bright-blue focus:ring-2 focus:ring-tracq-bright-blue/20
                          transition-all duration-200 outline-none"
                onChange={(e) => {
                  handleChange("aspect", e.target.value);
                }}
                placeholder="Category or aspect"
                value={form.aspect ?? ""}
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-300 block">Status</label>
              <input
                className="w-full px-4 py-3 bg-slate-700/50 border border-slate-500/50
                          rounded-xl text-white placeholder-slate-400
                          focus:border-tracq-bright-blue focus:ring-2 focus:ring-tracq-bright-blue/20
                          transition-all duration-200 outline-none"
                onChange={(e) => {
                  handleChange("status", e.target.value);
                }}
                placeholder="Task status"
                value={form.status ?? ""}
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-300 block">Deadline</label>
              <input
                className="w-full px-4 py-3 bg-slate-700/50 border border-slate-500/50
                          rounded-xl text-white
                          focus:border-tracq-bright-blue focus:ring-2 focus:ring-tracq-bright-blue/20
                          transition-all duration-200 outline-none"
                onChange={(e) => {
                  handleChange("deadline", e.target.value);
                }}
                type="date"
                value={form.deadline ?? ""}
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-300 block">Score</label>
              <input
                className="w-full px-4 py-3 bg-slate-700/50 border border-slate-500/50
                          rounded-xl text-white placeholder-slate-400
                          focus:border-tracq-bright-blue focus:ring-2 focus:ring-tracq-bright-blue/20
                          transition-all duration-200 outline-none"
                onChange={(e) => {
                  handleChange("score", Number(e.target.value));
                }}
                placeholder="Task score"
                type="number"
                value={form.score ?? ""}
              />
            </div>
          </div>

          <div className="flex justify-between items-center pt-8 mt-8 border-t border-slate-600/50">
            {isEdit && onDelete ? (
              <Button
                className="px-4 py-2"
                onClick={() => {
                  void handleDelete();
                }}
                variant="danger"
              >
                Delete
              </Button>
            ) : null}
            <div className="flex gap-3 ml-auto">
              <Button className="px-6 py-2" onClick={onClose} variant="secondary">
                Cancel
              </Button>
              <Button
                className="px-6 py-2"
                onClick={() => {
                  void handleSubmit();
                }}
                variant="primary"
              >
                {isEdit ? "Update" : "Create"}
              </Button>
            </div>
          </div>
        </DialogPanel>
      </div>
    </Dialog>
  );
};
